import React from 'react';
import {
  Tag,
  UserPlus,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  CalendarClock,
  Repeat,
  MoreVertical,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { KanbanCardData, BoardMember, KanbanLabel } from 'types/kanban';
import type { RecurrencePresetId } from '../../kanbanRecurrence';
import { getMemberColor, getInitials } from '../../kanbanUtils';
import { DESCRIPTION_PREVIEW_LENGTH } from '../../kanbanUtils';
import {
  useCurrentUserAvatar,
  isCurrentUser as isCurrentUserAvatar,
} from '../../useCurrentUserAvatar';
import { isCommentHtml } from 'utils/commentHtml';
import { sanitizeCommentHtml } from 'utils/commentHtml';
import { datetimeLocalToIso, formatIntuitiveDueAt } from '../../kanbanDueDate';
import KanbanConfirmModal from '../../KanbanConfirmModal';
import CommentEditorToolbar from '../CommentEditorToolbar';
import styles from './CardDetailLeftColumn.module.scss';

export interface CardDetailLeftColumnProps {
  card: KanbanCardData;
  previousCardId: string | null;
  onPreviousCard: () => void;
  nextCardId: string | null;
  onNextCard: () => void;
  editingCardTitle: boolean;
  cardTitleDraft: string;
  setCardTitleDraft: (v: string) => void;
  setEditingCardTitle: (v: boolean) => void;
  cardTitleInputRef: React.RefObject<HTMLInputElement | null>;
  onSaveCardTitle: () => void;
  onCancelCardTitle: () => void;
  labelsPanelOpen: boolean;
  onLabelsClick: () => void;
  labelsBtnRef: React.RefObject<HTMLButtonElement | null>;
  membersPanelOpen: boolean;
  onMembersClick: () => void;
  membersBtnRef: React.RefObject<HTMLButtonElement | null>;
  onAddMemberClick: () => void;
  membersAddBtnRef: React.RefObject<HTMLButtonElement | null>;
  assignedMembers: BoardMember[];
  assignedLabels: KanbanLabel[];
  editingDescription: boolean;
  descriptionDraft: string;
  setDescriptionDraft: (v: string) => void;
  descriptionTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onDescBold: () => void;
  onDescItalic: () => void;
  onDescStrikethrough: () => void;
  onDescCode: () => void;
  onDescClearFormatting: () => void;
  onDescBulletList: () => void;
  onDescNumberedList: () => void;
  onEditDescription: () => void;
  onSaveDescription: () => void;
  onBlurDescription: () => void;
  onCancelDescription: () => void;
  showFullDescription: boolean;
  setShowFullDescription: (v: boolean | ((prev: boolean) => boolean)) => void;
  checklist: Array<{ id: string; text: string; checked: boolean }>;
  checklistPct: number;
  newChecklistItemText: string;
  setNewChecklistItemText: (v: string) => void;
  onToggleChecklistItem: (itemId: string) => void;
  onUpdateChecklistItemText: (itemId: string, text: string) => void;
  onRemoveChecklistItem: (itemId: string) => void;
  onAddChecklistItem: () => void;
  onDeleteChecklist: () => void;
  showStartDateSection: boolean;
  onExpandStartDateForm: () => void;
  onCollapseStartDateForm: () => void;
  startDateLocal: string;
  onStartDateLocalChange: (value: string) => void;
  onStartDateBlur: () => void;
  onClearStartDate: () => void;
  showDueDateSection: boolean;
  onExpandDueDateForm: () => void;
  onCollapseDueDateForm: () => void;
  dueDateLocal: string;
  onDueDateLocalChange: (value: string) => void;
  onDueDateBlur: () => void;
  reminderLeadValue: number;
  onReminderLeadValueChange: (value: number) => void;
  onReminderLeadBlur: () => void;
  reminderLeadUnit: 'days' | 'hours' | 'minutes' | 'seconds';
  onReminderLeadUnitChange: (
    unit: 'days' | 'hours' | 'minutes' | 'seconds',
  ) => void;
  recurrencePreset: RecurrencePresetId;
  onRecurrencePresetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  recurrenceDisabled: boolean;
  recurrenceSummary: string;
  onScheduleNextDue: () => void;
  canScheduleNextDue: boolean;
  onClearDueDate: () => void;
}

export default function CardDetailLeftColumn({
  card,
  previousCardId,
  onPreviousCard,
  nextCardId,
  onNextCard,
  editingCardTitle,
  cardTitleDraft,
  setCardTitleDraft,
  setEditingCardTitle,
  cardTitleInputRef,
  onSaveCardTitle,
  onCancelCardTitle,
  labelsPanelOpen,
  onLabelsClick,
  labelsBtnRef,
  membersPanelOpen,
  onMembersClick,
  membersBtnRef,
  onAddMemberClick,
  membersAddBtnRef,
  assignedMembers,
  assignedLabels,
  editingDescription,
  descriptionDraft,
  setDescriptionDraft,
  descriptionTextareaRef,
  onDescBold,
  onDescItalic,
  onDescStrikethrough,
  onDescCode,
  onDescClearFormatting,
  onDescBulletList,
  onDescNumberedList,
  onEditDescription,
  onSaveDescription,
  onBlurDescription,
  onCancelDescription,
  showFullDescription,
  setShowFullDescription,
  checklist,
  checklistPct,
  newChecklistItemText,
  setNewChecklistItemText,
  onToggleChecklistItem,
  onUpdateChecklistItemText,
  onRemoveChecklistItem,
  onAddChecklistItem,
  onDeleteChecklist,
  showStartDateSection,
  onExpandStartDateForm,
  onCollapseStartDateForm,
  startDateLocal,
  onStartDateLocalChange,
  onStartDateBlur,
  onClearStartDate,
  showDueDateSection,
  onExpandDueDateForm,
  onCollapseDueDateForm,
  dueDateLocal,
  onDueDateLocalChange,
  onDueDateBlur,
  reminderLeadValue,
  onReminderLeadValueChange,
  onReminderLeadBlur,
  reminderLeadUnit,
  onReminderLeadUnitChange,
  recurrencePreset,
  onRecurrencePresetChange,
  recurrenceDisabled,
  recurrenceSummary,
  onScheduleNextDue,
  canScheduleNextDue,
  onClearDueDate,
}: CardDetailLeftColumnProps) {
  const { currentUserIds, profilePictureUrl } = useCurrentUserAvatar();
  const [clearDateTarget, setClearDateTarget] = React.useState<
    'start' | 'due' | null
  >(null);
  const [openDateActionsMenu, setOpenDateActionsMenu] = React.useState<
    'start' | 'due' | null
  >(null);
  const startMenuRef = React.useRef<HTMLDivElement | null>(null);
  const dueMenuRef = React.useRef<HTMLDivElement | null>(null);
  const hasDueDisplay =
    Boolean(dueDateLocal.trim()) || (card.dueAt != null && card.dueAt !== '');
  const hasStartDisplay =
    Boolean(startDateLocal.trim()) ||
    (card.startAt != null && card.startAt !== '');
  const startDisplayIso = startDateLocal.trim()
    ? datetimeLocalToIso(startDateLocal.trim())
    : card.startAt && card.startAt !== ''
      ? card.startAt
      : '';
  const dueDisplayIso = dueDateLocal.trim()
    ? datetimeLocalToIso(dueDateLocal.trim())
    : card.dueAt && card.dueAt !== ''
      ? card.dueAt
      : '';
  const startDisplayFormatted = startDisplayIso
    ? formatIntuitiveDueAt(startDisplayIso)
    : '';
  const dueDisplayFormatted = dueDisplayIso
    ? formatIntuitiveDueAt(dueDisplayIso)
    : '';
  const showStartDateBlock = hasStartDisplay || showStartDateSection;
  const showDueDateBlock = hasDueDisplay || showDueDateSection;

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (openDateActionsMenu === 'start') {
        if (!startMenuRef.current?.contains(event.target as Node)) {
          setOpenDateActionsMenu(null);
        }
      }
      if (openDateActionsMenu === 'due') {
        if (!dueMenuRef.current?.contains(event.target as Node)) {
          setOpenDateActionsMenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openDateActionsMenu]);

  return (
    <>
      {editingCardTitle ? (
        <input
          ref={cardTitleInputRef}
          type="text"
          className={styles.cardDetailTitleInput}
          value={cardTitleDraft}
          onChange={(e) => setCardTitleDraft(e.target.value)}
          onBlur={onSaveCardTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSaveCardTitle();
            if (e.key === 'Escape') onCancelCardTitle();
          }}
          aria-label="Card title"
        />
      ) : (
        <h2
          className={styles.cardDetailTitle}
          role="button"
          tabIndex={0}
          onClick={() => {
            setCardTitleDraft(card.title);
            setEditingCardTitle(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setCardTitleDraft(card.title);
              setEditingCardTitle(true);
            }
          }}
        >
          {card.title}
        </h2>
      )}
      <div className={styles.cardDetailActions}>
        <button
          ref={labelsBtnRef}
          type="button"
          className={`${styles.cardActionBtn} ${labelsPanelOpen ? styles.cardActionBtnActive : ''}`}
          onClick={onLabelsClick}
        >
          <Tag size={16} />
          Labels
        </button>
        <button
          ref={membersBtnRef}
          type="button"
          className={`${styles.cardActionBtn} ${membersPanelOpen ? styles.cardActionBtnActive : ''}`}
          onClick={onMembersClick}
        >
          <UserPlus size={16} />
          Members
        </button>
      </div>
      <div className={styles.cardDetailDueSection}>
        {showStartDateBlock ? (
          <div className={styles.dateBlock}>
            {hasStartDisplay ? (
              <div className={styles.dateRow}>
                <div className={styles.dateInfo}>
                  <div className={styles.dateInline}>
                    <h3 className={styles.modalSectionTitle}>
                      <CalendarClock size={16} aria-hidden />
                      Start date
                    </h3>
                    {startDisplayFormatted ? (
                      <p className={styles.dateValue}>
                        {startDisplayFormatted}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className={styles.dateMenuWrap} ref={startMenuRef}>
                  <button
                    type="button"
                    className={styles.dateMenuTrigger}
                    onClick={() =>
                      setOpenDateActionsMenu((prev) =>
                        prev === 'start' ? null : 'start',
                      )
                    }
                    aria-label="Start date actions"
                    aria-haspopup="menu"
                    aria-expanded={openDateActionsMenu === 'start'}
                  >
                    <MoreVertical size={16} />
                  </button>
                  {openDateActionsMenu === 'start' ? (
                    <div className={styles.dateActionsMenu} role="menu">
                      <button
                        type="button"
                        className={styles.dateActionsItem}
                        onClick={() => {
                          onExpandStartDateForm();
                          setOpenDateActionsMenu(null);
                        }}
                        role="menuitem"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={styles.dateActionsItemDanger}
                        onClick={() => {
                          setOpenDateActionsMenu(null);
                          setClearDateTarget('start');
                        }}
                        role="menuitem"
                      >
                        Clear
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {showStartDateSection ? (
              <>
                <label
                  className={styles.dueLabel}
                  htmlFor={`card-start-${card.id}`}
                >
                  Date and time
                </label>
                <input
                  id={`card-start-${card.id}`}
                  type="datetime-local"
                  className={styles.dueDatetimeInput}
                  value={startDateLocal}
                  onChange={(e) => onStartDateLocalChange(e.target.value)}
                  onBlur={onStartDateBlur}
                />
                <button
                  type="button"
                  className={`${styles.dueCompactBtn} ${styles.dateDoneBtn}`}
                  onClick={onCollapseStartDateForm}
                >
                  Done
                </button>
              </>
            ) : null}
          </div>
        ) : null}
        {showDueDateBlock ? (
          <div className={styles.dateBlock}>
            {hasDueDisplay ? (
              <div className={styles.dateRow}>
                <div className={styles.dateInfo}>
                  <div className={styles.dateInline}>
                    <h3 className={styles.modalSectionTitle}>
                      <CalendarClock size={16} aria-hidden />
                      Due date
                    </h3>
                    {dueDisplayFormatted ? (
                      <p className={styles.dateValue}>{dueDisplayFormatted}</p>
                    ) : null}
                  </div>
                </div>
                <div className={styles.dateMenuWrap} ref={dueMenuRef}>
                  <button
                    type="button"
                    className={styles.dateMenuTrigger}
                    onClick={() =>
                      setOpenDateActionsMenu((prev) =>
                        prev === 'due' ? null : 'due',
                      )
                    }
                    aria-label="Due date actions"
                    aria-haspopup="menu"
                    aria-expanded={openDateActionsMenu === 'due'}
                  >
                    <MoreVertical size={16} />
                  </button>
                  {openDateActionsMenu === 'due' ? (
                    <div className={styles.dateActionsMenu} role="menu">
                      <button
                        type="button"
                        className={styles.dateActionsItem}
                        onClick={() => {
                          onExpandDueDateForm();
                          setOpenDateActionsMenu(null);
                        }}
                        role="menuitem"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={styles.dateActionsItemDanger}
                        onClick={() => {
                          setOpenDateActionsMenu(null);
                          setClearDateTarget('due');
                        }}
                        role="menuitem"
                      >
                        Clear
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {showDueDateSection ? (
              <>
                <p className={styles.dueHelpText}>
                  Optional. The board shows a due indicator within the reminder
                  window before the deadline (default 14 days).
                </p>
                <label
                  className={styles.dueLabel}
                  htmlFor={`card-due-${card.id}`}
                >
                  Date and time
                </label>
                <input
                  id={`card-due-${card.id}`}
                  type="datetime-local"
                  className={styles.dueDatetimeInput}
                  value={dueDateLocal}
                  onChange={(e) => onDueDateLocalChange(e.target.value)}
                  onBlur={onDueDateBlur}
                />
                <div className={styles.reminderRow}>
                  <label
                    className={styles.dueLabel}
                    htmlFor={`card-reminder-${card.id}`}
                  >
                    Remind me within
                  </label>
                  <div className={styles.reminderInputs}>
                    <input
                      id={`card-reminder-${card.id}`}
                      type="number"
                      min={1}
                      className={styles.reminderNumber}
                      value={reminderLeadValue}
                      onChange={(e) =>
                        onReminderLeadValueChange(
                          Math.max(1, Number.parseInt(e.target.value, 10) || 1),
                        )
                      }
                      onBlur={onReminderLeadBlur}
                      disabled={!dueDateLocal.trim() && !card.dueAt}
                    />
                    <select
                      className={styles.reminderUnit}
                      value={reminderLeadUnit}
                      onChange={(e) =>
                        onReminderLeadUnitChange(
                          e.target.value as
                            'days' | 'hours' | 'minutes' | 'seconds',
                        )
                      }
                      disabled={!dueDateLocal.trim() && !card.dueAt}
                    >
                      <option value="days">days</option>
                      <option value="hours">hours</option>
                      <option value="minutes">minutes</option>
                      <option value="seconds">seconds</option>
                    </select>
                  </div>
                </div>
                <div className={styles.recurrenceBlock}>
                  <label
                    className={styles.dueLabel}
                    htmlFor={`card-repeat-${card.id}`}
                  >
                    <Repeat
                      size={14}
                      aria-hidden
                      className={styles.recurrenceIcon}
                    />
                    Repeat
                  </label>
                  <select
                    id={`card-repeat-${card.id}`}
                    className={styles.recurrenceSelect}
                    value={recurrencePreset}
                    onChange={onRecurrencePresetChange}
                    disabled={recurrenceDisabled}
                    aria-label="Repeat schedule"
                    title={
                      recurrenceDisabled
                        ? 'Set a due date to enable repeating tasks'
                        : recurrenceSummary || undefined
                    }
                  >
                    <option value="none">Does not repeat</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every 2 weeks</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  {recurrenceDisabled ? (
                    <p className={styles.recurrenceHint}>
                      Set a due date above to use repeating tasks (optional).
                    </p>
                  ) : null}
                  {recurrenceSummary && !recurrenceDisabled ? (
                    <p className={styles.recurrenceSummary}>
                      {recurrenceSummary}
                    </p>
                  ) : null}
                  {canScheduleNextDue ? (
                    <button
                      type="button"
                      className={styles.scheduleNextBtn}
                      onClick={onScheduleNextDue}
                    >
                      Schedule next due
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={`${styles.dueCompactBtn} ${styles.dateDoneBtn}`}
                  onClick={onCollapseDueDateForm}
                >
                  Done
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={styles.cardDetailMembersSection}>
        <h3 className={styles.modalSectionTitle}>Members</h3>
        <div className={styles.membersSectionRow}>
          {assignedMembers.slice(0, 4).map((member) => {
            const resolvedAvatarUrl =
              member.profilePictureUrl ??
              (isCurrentUserAvatar(currentUserIds, member.userId)
                ? profilePictureUrl
                : null);
            return resolvedAvatarUrl ? (
              <img
                key={member.id}
                src={resolvedAvatarUrl}
                alt=""
                className={styles.memberAvatarImg}
                title={member.name}
                aria-hidden
              />
            ) : (
              <span
                key={member.id}
                className={styles.memberAvatar}
                style={{
                  background: getMemberColor(member.id),
                  color: 'white',
                }}
                title={member.name}
              >
                {getInitials(member.name)}
              </span>
            );
          })}
          {assignedMembers.length > 4 && (
            <span className={styles.memberAvatarMore}>
              +{assignedMembers.length - 4}
            </span>
          )}
          {assignedLabels.map((label) => (
            <span
              key={label.id}
              className={styles.memberLabelChip}
              style={{ background: label.color }}
              title={label.name}
            >
              {label.name}
            </span>
          ))}
          <button
            ref={membersAddBtnRef}
            type="button"
            className={styles.memberAddBtn}
            onClick={onAddMemberClick}
            aria-label="Add member"
          >
            <Plus size={14} />
          </button>
        </div>
        {assignedMembers.length === 0 && (
          <p className={styles.membersSectionHint}>
            No members assigned. Click + to assign from the board.
          </p>
        )}
      </div>
      <div className={styles.cardDetailDescription}>
        <h3 className={styles.modalSectionTitle}>Description</h3>
        {editingDescription ? (
          <>
            <div className={styles.descriptionEditorWrap}>
              <div
                className={styles.descriptionToolbarWrap}
                onMouseDown={(e) => e.preventDefault()}
                role="presentation"
              >
                <CommentEditorToolbar
                  onBold={onDescBold}
                  onItalic={onDescItalic}
                  onStrikethrough={onDescStrikethrough}
                  onCode={onDescCode}
                  onClearFormatting={onDescClearFormatting}
                  onBulletList={onDescBulletList}
                  onNumberedList={onDescNumberedList}
                />
              </div>
              <textarea
                ref={descriptionTextareaRef}
                className={styles.inputDescriptionMd}
                value={descriptionDraft}
                onChange={(e) => setDescriptionDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    onSaveDescription();
                  }
                }}
                onBlur={onBlurDescription}
                placeholder="Add a more detailed description... (Markdown supported)"
                aria-label="Description"
                rows={5}
              />
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={`${styles.formBtn} ${styles.primary}`}
                onClick={onSaveDescription}
              >
                Save
              </button>
              <button
                type="button"
                className={`${styles.formBtn} ${styles.secondary}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={onCancelDescription}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div
            className={`${styles.descriptionBlock} ${!card.description ? styles.descriptionBlockEmpty : ''}`}
            onClick={onEditDescription}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEditDescription();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Edit description"
          >
            <div
              className={styles.descriptionContent}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('a')) {
                  e.stopPropagation();
                }
              }}
              role="presentation"
            >
              {card.description ? (
                <>
                  {isCommentHtml(card.description) ? (
                    <div
                      className={styles.descriptionHtml}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeCommentHtml(
                          showFullDescription ||
                            card.description.length <=
                              DESCRIPTION_PREVIEW_LENGTH
                            ? card.description
                            : `${card.description.slice(0, DESCRIPTION_PREVIEW_LENGTH)}…`,
                        ),
                      }}
                    />
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ ...props }) => (
                          <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {props.children}
                          </a>
                        ),
                      }}
                    >
                      {card.description.length > DESCRIPTION_PREVIEW_LENGTH &&
                      !showFullDescription
                        ? `${card.description.slice(0, DESCRIPTION_PREVIEW_LENGTH)}…`
                        : card.description}
                    </ReactMarkdown>
                  )}
                  {card.description.length > DESCRIPTION_PREVIEW_LENGTH && (
                    <button
                      type="button"
                      className={styles.descriptionShowMoreBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullDescription((v) => !v);
                      }}
                    >
                      {showFullDescription ? (
                        <>
                          <ChevronUp size={16} />
                          Show less
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Show more
                        </>
                      )}
                    </button>
                  )}
                </>
              ) : (
                <p className={styles.descriptionPlaceholder}>
                  Add a more detailed description...
                </p>
              )}
            </div>
            {card.description && (
              <button
                type="button"
                className={styles.editDescriptionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditDescription();
                }}
              >
                Edit
              </button>
            )}
          </div>
        )}
        <div className={styles.nextCardRow}>
          {previousCardId ? (
            <button
              type="button"
              className={`${styles.formBtn} ${styles.secondary} ${styles.nextCardBtn}`}
              onClick={() => onPreviousCard()}
              disabled={editingDescription}
              aria-label="Previous card"
            >
              Previous card
            </button>
          ) : nextCardId ? (
            <span aria-hidden />
          ) : null}
          {nextCardId && (
            <button
              type="button"
              className={`${styles.formBtn} ${styles.secondary} ${styles.nextCardBtn}`}
              onClick={() => onNextCard()}
              disabled={editingDescription}
              aria-label="Next card"
            >
              Next card
            </button>
          )}
        </div>
      </div>
      {checklist.length > 0 && (
        <div className={styles.checklistSection}>
          <div className={styles.checklistHeader}>
            <h3 className={styles.modalSectionTitle}>Checklist</h3>
            <span className={styles.checklistProgress}>{checklistPct}%</span>
          </div>
          <div
            className={styles.checklistProgressBar}
            role="progressbar"
            aria-valuenow={checklistPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={styles.checklistProgressFill}
              style={{ width: `${checklistPct}%` }}
            />
          </div>
          <ul className={styles.checklistList}>
            {checklist.map((item) => (
              <li key={item.id} className={styles.checklistItem}>
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={() => onToggleChecklistItem(item.id)}
                  className={styles.checklistCheckbox}
                  aria-label={item.text || 'Checklist item'}
                />
                <input
                  type="text"
                  className={styles.checklistItemInput}
                  value={item.text}
                  onChange={(e) =>
                    onUpdateChecklistItemText(item.id, e.target.value)
                  }
                  placeholder="Item text"
                  aria-label="Checklist item text"
                />
                <button
                  type="button"
                  className={styles.checklistItemRemove}
                  onClick={() => onRemoveChecklistItem(item.id)}
                  aria-label="Remove item"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
          <div className={styles.checklistActions}>
            <input
              type="text"
              className={styles.checklistAddInput}
              placeholder="Add an item"
              value={newChecklistItemText}
              onChange={(e) => setNewChecklistItemText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onAddChecklistItem();
                }
              }}
              aria-label="New checklist item"
            />
            <button
              type="button"
              className={`${styles.formBtn} ${styles.primary}`}
              onClick={onAddChecklistItem}
              disabled={!newChecklistItemText.trim()}
            >
              Add
            </button>
          </div>
          <button
            type="button"
            className={styles.deleteChecklistBtn}
            onClick={onDeleteChecklist}
          >
            <Trash2 size={14} />
            Delete checklist
          </button>
        </div>
      )}
      <KanbanConfirmModal
        show={clearDateTarget != null}
        onHide={() => setClearDateTarget(null)}
        title={
          clearDateTarget === 'start'
            ? 'Clear start date'
            : clearDateTarget === 'due'
              ? 'Clear due date'
              : 'Clear date'
        }
        confirmLabel="Clear"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (clearDateTarget === 'start') onClearStartDate();
          if (clearDateTarget === 'due') onClearDueDate();
          setClearDateTarget(null);
        }}
      >
        {clearDateTarget === 'start'
          ? 'Are you sure you want to clear the start date?'
          : 'Are you sure you want to clear the due date?'}
      </KanbanConfirmModal>
    </>
  );
}
